import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Bike, Plus } from 'lucide-react';

const Riders = ({ user, onLogout }) => {
  const [riders, setRiders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newRider, setNewRider] = useState({
    user_id: '',
    vehicle_type: 'bike',
    license_number: ''
  });

  const fetchRiders = useCallback(async () => {
    try {
      const response = await axios.get('/riders');
      setRiders(response.data);
    } catch (error) {
      toast.error('Error al cargar riders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  const handleCreateRider = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/riders', newRider);
      toast.success('¡Rider creado!');
      setShowDialog(false);
      fetchRiders();
      setNewRider({ user_id: '', vehicle_type: 'bike', license_number: '' });
    } catch (error) {
      toast.error('Error al crear rider');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Riders">
      <div className="space-y-6" data-testid="riders-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#224049' }}>Riders</h1>
            <p className="text-sm" style={{ color: '#666' }}>Gestiona los repartidores</p>
          </div>
          {user.role === 'admin' && (
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button style={{ backgroundColor: '#224049', color: '#D4AD6A' }} data-testid="add-rider-button">
                  <Plus className="w-4 h-4 mr-2" /> Agregar Rider
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuevo Rider</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateRider} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="rider-user">Usuario ID</Label>
                    <Input
                      id="rider-user"
                      data-testid="rider-user-input"
                      placeholder="ID del usuario con rol rider"
                      value={newRider.user_id}
                      onChange={(e) => setNewRider({ ...newRider, user_id: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rider-vehicle">Tipo de Vehículo</Label>
                    <Select
                      value={newRider.vehicle_type}
                      onValueChange={(value) => setNewRider({ ...newRider, vehicle_type: value })}
                    >
                      <SelectTrigger data-testid="rider-vehicle-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bike">Bicicleta</SelectItem>
                        <SelectItem value="scooter">Scooter</SelectItem>
                        <SelectItem value="car">Automóvil</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rider-license">Número de Licencia</Label>
                    <Input
                      id="rider-license"
                      data-testid="rider-license-input"
                      value={newRider.license_number}
                      onChange={(e) => setNewRider({ ...newRider, license_number: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full" style={{ backgroundColor: '#D9A27F', color: 'white' }} data-testid="submit-rider-button">
                    Crear Rider
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg" style={{ color: '#224049' }}>Cargando...</div>
          </div>
        ) : riders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bike className="w-16 h-16 mx-auto mb-4" style={{ color: '#D9A27F' }} />
              <p className="text-lg" style={{ color: '#666' }}>No hay riders registrados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {riders.map((rider) => (
              <Card key={rider.id} className="card fade-in shadow-lg hover:shadow-xl" data-testid={`rider-card-${rider.id}`}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="p-3 rounded-full" style={{ backgroundColor: '#224049' }}>
                      <Bike className="w-6 h-6" style={{ color: '#D4AD6A' }} />
                    </div>
                    <div>
                      <CardTitle style={{ color: '#224049' }}>Rider {rider.id.substring(0, 8)}</CardTitle>
                      <p className="text-sm" style={{ color: '#666' }}>{rider.vehicle_type}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium" style={{ color: '#666' }}>Entregas</p>
                      <p className="text-xl font-bold" style={{ color: '#224049' }}>{rider.total_deliveries}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: '#666' }}>Rating</p>
                      <p className="text-xl font-bold" style={{ color: '#D4AD6A' }}>{rider.rating.toFixed(1)} ⭐</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#666' }}>Pedidos Activos</p>
                    <p className="text-lg font-semibold" style={{ color: '#D9A27F' }}>{rider.active_orders}</p>
                  </div>
                  <div>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${rider.is_available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {rider.is_available ? 'Disponible' : 'No Disponible'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Riders;